import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';

/**
 * GOLD STANDARD for searchable dropdowns that fetch their options from an API.
 *
 * Use this for every antd `Select` with `showSearch` + `onSearch` whose `onSearch`
 * triggers a network call. It:
 *   1. Debounces the search input so the API fires once the user pauses typing
 *      (not on every keystroke), and
 *   2. Exposes a `loading` flag that stays true from the first keystroke until the
 *      fetch promise settles — wire it to the `Select`'s `loading` prop and
 *      `notFoundContent` so the user always sees a spinner while results load.
 *
 * Usage:
 *   const { handleSearch, loading } = useDebouncedSearch(
 *     (value) => dispatch(getAllMaterialslist({ search: value }))
 *   );
 *
 *   <Select
 *     showSearch
 *     onSearch={handleSearch}
 *     loading={loading}
 *     notFoundContent={loading ? <Spin size="small" /> : null}
 *     filterOption={false}   // server-side search — don't also filter locally
 *   >
 *     ...
 *   </Select>
 *
 * @param {(searchValue: string) => Promise<any>} fetcher
 *        Async fn that performs the API call. Typically returns `dispatch(thunk(...))`,
 *        whose promise resolves when the request settles.
 * @param {object}  [options]
 * @param {number}  [options.delay=400]         Debounce delay in ms.
 * @param {number}  [options.minChars=0]        Min characters before firing (0 = always).
 * @param {boolean} [options.fetchOnEmpty=true] Whether to fetch when the input is cleared.
 * @returns {{ handleSearch: (value: string) => void, loading: boolean, cancel: () => void }}
 */
const useDebouncedSearch = (fetcher, options = {}) => {
  const { delay = 400, minChars = 0, fetchOnEmpty = true } = options;

  const [loading, setLoading] = useState(false);

  // Keep the latest fetcher in a ref so the debounced fn doesn't need to be
  // re-created when the caller passes a fresh closure each render (e.g. one that
  // closes over component state). This keeps the debounce timer stable.
  const fetcherRef = useRef(fetcher);
  useEffect(() => {
    fetcherRef.current = fetcher;
  });

  // Only the most recent request is allowed to clear `loading`, so fast typing
  // never turns the spinner off early or makes it flicker.
  const requestId = useRef(0);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const run = useCallback(async (value) => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      await fetcherRef.current(value);
    } finally {
      if (mounted.current && id === requestId.current) {
        setLoading(false);
      }
    }
  }, []);

  const debounced = useMemo(
    () => debounce((value) => run(value), delay),
    [run, delay]
  );

  // Cancel any pending call when the delay changes or the component unmounts.
  useEffect(() => () => debounced.cancel(), [debounced]);

  const handleSearch = useCallback(
    (value) => {
      const trimmed = (value ?? '').trim();

      if (minChars && trimmed.length < minChars) {
        debounced.cancel();
        setLoading(false);
        return;
      }
      if (!trimmed && !fetchOnEmpty) {
        debounced.cancel();
        setLoading(false);
        return;
      }

      // Show the loader immediately — it covers both the debounce wait and the
      // in-flight request, and is cleared by `run` once the API settles.
      setLoading(true);
      debounced(trimmed);
    },
    [debounced, minChars, fetchOnEmpty]
  );

  return { handleSearch, loading, cancel: debounced.cancel };
};

export default useDebouncedSearch;
